import { useState } from "react";

import { Check, ChevronDown, Search } from "lucide-react";
import { dialCountryCodes } from "utils/constants";

import { Button } from "js/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "js/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";

import { FlagEmoji } from "./FlagEmoji";

interface CountryCodeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
}

export const CountryCodeSelect = ({
  value,
  onValueChange,
}: CountryCodeSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedCountry = dialCountryCodes.find(
    (country: any) => country.code === value,
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-11 justify-between bg-white border-gray-200 hover:bg-gray-50 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 px-3"
        >
          <div className="flex items-center gap-2">
            {selectedCountry && (
              <FlagEmoji
                countryCode={selectedCountry.iso}
                className="text-base flex-shrink-0"
              />
            )}
            <span className="font-medium">
              {selectedCountry ? selectedCountry.code : "+52"}
            </span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-[9999]" align="start">
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search country..."
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              value={searchValue}
              onValueChange={setSearchValue}
            />
          </div>
          <CommandList className="max-h-60 overflow-y-auto">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {dialCountryCodes.map((country: any) => (
                <CommandItem
                  key={country.iso}
                  value={`${country.country} ${country.code}`}
                  onSelect={() => {
                    onValueChange(country.code);
                    setOpen(false);
                    setSearchValue("");
                  }}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                >
                  <FlagEmoji
                    countryCode={country.iso}
                    className="text-base flex-shrink-0"
                  />
                  <div className="flex items-center justify-between flex-1">
                    <span className="text-sm text-gray-700 truncate">
                      {country.country}
                    </span>
                    <span className="font-medium text-gray-900 ml-2">
                      {country.code}
                    </span>
                  </div>
                  {value === country.code && (
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
